import { useState, useEffect } from "react";
import styled, { px, seconds } from "livemap-ui";

import { Slider } from "./components/Slider";
import { useAddToast } from "./contexts/toast";
import { EmailForm } from "./components/EmailForm";
import { CellPhoneForm, PhoneNumberData } from "./components/CellPhoneForm";
import { MainHeader } from "./components/MainHeader";
import { NameForm, NameData } from "./components/NameForm";
import { useHistory, useParams } from "react-router";
import {
  registerPerson,
  Person,
  checkEmail,
  checkPhoneNumber,
} from "./api/person";
import { UserInfo as WelcomeCard } from "./components/WelcomeCard";
import { getErrorDescription } from "./error";

interface SignUpParams {
  stepNumber?: string;
}

const visibleSteps = 3;

export function SignUp() {
  const addToast = useAddToast();
  const { push } = useHistory();
  const { stepNumber = "1" } = useParams<SignUpParams>();
  const step = Number(stepNumber) - 1;
  const setStep = (index: number) => push(`/sign-up/${index + 1}`);

  const [isSending, setIsSending] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState<PhoneNumberData | null>(null);
  const [names, setNames] = useState<NameData | null>(null);
  const [user, setUser] = useState<Person | null>(null);

  /*
    TODO: Refactor this.
    It's breaking a lot of rules of hooks
  */

  useEffect(() => {
    if (step >= 1 && !email) {
      setPhone(null);
      setNames(null);
      push("/sign-up/1");
    } else if (step >= 2 && !phone) {
      setNames(null);
      push("/sign-up/2");
    } else if (step >= 3 && !names) {
      push("/sign-up/3");
    }
  }, [step]);

  useEffect(() => {
    if (email.length === 0) {
      return;
    }

    setIsSending(true);

    checkEmail(email)
      .then((exists) => {
        if (exists) {
          addToast({
            id: "account-email-exists",
            title: "Account exists",
            text: "An account with that E-mail address already exists. Do you want to sign in instead?",
            type: "info",
            actions: [{ text: "Sign in", onClick: () => push("/sign-in") }],
            duration: seconds(10),
          });
        } else {
          setStep(step + 1);
        }

        setIsSending(false);
      })
      .catch((err) => {
        setIsSending(false);

        addToast({
          id: "api-error",
          title: "API error",
          text: getErrorDescription(err),
          type: "danger",
        });
      });
  }, [email]);

  useEffect(() => {
    if (!phone) {
      return;
    }

    setIsSending(true);

    checkPhoneNumber(phone.dialingCode, phone.cellPhone)
      .then((exists) => {
        if (exists) {
          addToast({
            id: "account-phone-exists",
            title: "Account exists",
            text: "An account with that phone number already exists. Do you want to sign in instead?",
            type: "info",
            actions: [{ text: "Sign in", onClick: () => push("/sign-in") }],
            duration: seconds(10),
          });
        } else {
          setStep(step + 1);
        }

        setIsSending(false);
      })
      .catch((err) => {
        setIsSending(false);

        addToast({
          id: "api-error",
          title: "API error",
          text: getErrorDescription(err),
          type: "danger",
        });
      });
  }, [phone]);

  useEffect(() => {
    if (names && email && phone) {
      setIsSending(true);

      registerPerson({ email, ...phone, ...names })
        .then((user) => {
          setUser(user);
          setIsSending(false);
          setStep(step + 1);
        })
        .catch((err) => {
          setIsSending(false);

          addToast({
            id: "api-error",
            title: "API error",
            text: getErrorDescription(err),
            type: "danger",
          });
        });
    }
  }, [names]);

  const isLastStep = step > visibleSteps - 1;

  const title = isLastStep ? "Welcome to Veridict!" : "Sign up";
  const stepTitle = isLastStep ? undefined : `Step ${step + 1}/${visibleSteps}`;

  return (
    <>
      <MainHeader title={title} subTitle={stepTitle} />

      <Slider step={step}>
        <Step>
          <Instruction>Enter your e-mail address</Instruction>
          <EmailForm
            isActive={step === 0}
            submitText="Continue"
            isSending={isSending}
            onSubmit={(form) => setEmail(form.email)}
          />
        </Step>

        <Step>
          <Instruction>Please enter your phone number</Instruction>
          <CellPhoneForm
            isActive={step === 1}
            submitText="Continue"
            isSending={isSending}
            onSubmit={(form) => setPhone(form)}
          />
        </Step>

        <Step>
          <Instruction>Please enter your name</Instruction>
          <NameForm
            isActive={step === 2}
            submitText="Complete"
            isSending={isSending}
            onSubmit={(form) => setNames(form)}
          />
        </Step>

        <Step>{user ? <WelcomeCard user={user} /> : null}</Step>
      </Slider>
    </>
  );
}

const Step = styled.div``;

const Instruction = styled.p`
  text-align: center;
  padding: ${px(0, 0, 32, 0)};
  line-height: 1;
  font-size: 16px;
  opacity: ${(p) => p.theme.disabledOpacity};
`;
